import { Trans } from 'react-i18next'
import {
  BussinessPlayerAccount,
  BussinessPlayerAccounts,
  PlayerAccountInviteStatus,
  Tag,
} from '../../../../types/commonTypes'
import styles from './PlayerAccountTable.module.css'
import { CellElement, TableRow } from '../../../reports/components/GenericTable'
import { getIcon } from '../../../../common/components/icons/utils'
import { TagPill } from '../../../../common/components/TagPill/TagPill'
import { Tooltip } from '../../../../common/components/tooltip/Tooltip'
import { dateTimeToDashDateString, softBreakToEmail } from '../../../../util/string'
import classNames from 'classnames'
import { TFunction } from 'i18next'

type getPlayerAccountTableRowProps = {
  businessPlayerAccounts: BussinessPlayerAccounts
  setShowLabelModal: (showLabel: boolean, playerAccount: BussinessPlayerAccount) => void
  onRenewInvite: (playerEmail: string) => void
  onDeletePlayerAccount: (playerId: number) => void
  t: TFunction
  hasLicences: boolean
}

type rowData = {
  rows: TableRow[]
  customClassRows: number[]
}

export const getPlayerAccountTableRow = (data: getPlayerAccountTableRowProps): rowData => {
  const { businessPlayerAccounts, setShowLabelModal, onRenewInvite, onDeletePlayerAccount, t, hasLicences } = data

  const onRenewInviterInternal = async (playerAccount: BussinessPlayerAccount) => {
    onRenewInvite(playerAccount.email)
  }

  const onRemovePlayerInternal = async (playerAccount: BussinessPlayerAccount) => {
    onDeletePlayerAccount(playerAccount.id)
  }

  const createActionCell = (playerAccount: BussinessPlayerAccount) => {
    return (
      <div
        className={classNames(
          styles.cell,
          playerAccount.inviteStatus === PlayerAccountInviteStatus.EXPIRED && styles.cellExp,
          styles.playerAccountActionsContainer,
        )}
      >
        <button onClick={() => setShowLabelModal(true, playerAccount)}>
          <span>{getIcon('text')}</span>
          <span>{t('settings_organization.player_accounts.action_text.assign_tag', 'Assign tag')}</span>
        </button>
        <Tooltip
          tooltipContent={t(
            'settings_organization.player_accounts.tooltip.renew_invite',
            'With renew invite you can resend player account link and password',
          )}
        >
          {(tooltipProps) => (
            <button onClick={() => onRenewInviterInternal(playerAccount)} {...tooltipProps}>
              <span>{getIcon('changeIcon')}</span>
              <span>{t('settings_organization.player_accounts.action_text.renew_invite', 'Renew invite')}</span>
            </button>
          )}
        </Tooltip>
        <button onClick={() => onRemovePlayerInternal(playerAccount)}>
          <span>{getIcon('trash')}</span>
          <span>{t('settings_organization.player_accounts.action_text.remove_player', 'Remove player')}</span>
        </button>
      </div>
    )
  }

  const createTagCell = (playerAccount: BussinessPlayerAccount) => {
    return (
      <div
        className={classNames(
          styles.cell,
          playerAccount.inviteStatus === PlayerAccountInviteStatus.EXPIRED && styles.cellExp,
          styles.tagCell,
        )}
      >
        {playerAccount.tags.map((dataTag, index) => {
          const tag = dataTag as Tag
          return <TagPill key={tag.id + '_tag_' + index} label={tag.label} size='small' />
        })}
      </div>
    )
  }

  const getTranslatedInviteStatus = (inviteStatus: PlayerAccountInviteStatus) => {
    switch (inviteStatus) {
      case PlayerAccountInviteStatus.ACCEPTED:
        return t('settings_organization.player_accounts.invite_status.accepted', 'Accepted')
      case PlayerAccountInviteStatus.EXPIRED:
        return t('settings_organization.player_accounts.invite_status.expired', 'Expired')
      case PlayerAccountInviteStatus.PENDING:
        return t('settings_organization.player_accounts.invite_status.pending', 'Pending')
    }
  }

  const createStatusCell = (playerAccount: BussinessPlayerAccount) => {
    return (
      <div
        className={classNames(
          styles.cell,
          playerAccount.inviteStatus === PlayerAccountInviteStatus.EXPIRED && styles.cellExp,
          styles.statusCell,
        )}
      >
        {
          /* Business has new model, player has accepted the invitation and has joined a game. A licence is used. 
					Status will say Accepted, and green checkmark is shown */
          playerAccount.inviteStatus === PlayerAccountInviteStatus.ACCEPTED && playerAccount.licenceUser ? (
            <Tooltip
              tooltipContent={t('settings_organization.player_accounts.tooltip.active_licence', 'Active licence')}
            >
              {(tooltipProps) => (
                <div className={styles.checkmark} {...tooltipProps}>
                  {getIcon('checkMark')}
                </div>
              )}
            </Tooltip>
          ) : /* Business does not have new model, and player has accepted the invitation. Account is "active" 
					and will not expire. Status will say Accepted and a green checkmark is shown */
          playerAccount.inviteStatus === PlayerAccountInviteStatus.ACCEPTED && !hasLicences ? (
            <Tooltip
              tooltipContent={t('settings_organization.player_accounts.tooltip.active_account', 'Active account')}
            >
              {(tooltipProps) => (
                <div className={styles.checkmark} {...tooltipProps}>
                  {getIcon('checkMark')}
                </div>
              )}
            </Tooltip>
          ) : (
            /* playerAccount.inviteStatus === PlayerAccountInviteStatus.EXPIRED: Over 72 hours has passed since 
						the invitation was send, and it has expired. Status will say Expired, a gray close button is shown 
						and the row has red background color and borders. These lines will be ordered to the top by default
					
						playerAccount.inviteStatus === PlayerAccountInviteStatus.PENDING: The invitation has been send less 
						than 72 hours ago, but the player has not yet confimed the account. Status text is Pending, and a 
						grey clock is shown
					
						other = status is Accepted, business has player licences and the player has not yet joined a game 
						- no player licences is used at this point: status will say Accepted, a grey checkmark is shown.
					*/
            <Tooltip
              tooltipContent={
                playerAccount.inviteStatus === PlayerAccountInviteStatus.EXPIRED
                  ? t(
                      'settings_organization.player_accounts.tooltip.expired_invite',
                      'Invite expired, account not activated',
                    )
                  : playerAccount.inviteStatus === PlayerAccountInviteStatus.PENDING
                  ? t(
                      'settings_organization.player_accounts.tooltip.pending_invite',
                      'The player hasn’t activated their account yet',
                    )
                  : t('settings_organization.player_accounts.tooltip.inactive_licence', 'No licence used')
              }
            >
              {(tooltipProps) => (
                <div className={styles.closemark} {...tooltipProps}>
                  {playerAccount.inviteStatus === PlayerAccountInviteStatus.EXPIRED
                    ? getIcon('close')
                    : playerAccount.inviteStatus === PlayerAccountInviteStatus.PENDING
                    ? getIcon('recents')
                    : getIcon('checkMark')}
                </div>
              )}
            </Tooltip>
          )
        }
        <div className={styles.statusCellTextContainer}>
          <p>{getTranslatedInviteStatus(playerAccount.inviteStatus)}</p>
          <Trans
            i18nKey='settings_organization.player_accounts.invite_send'
            defaults='Invite send:<br/> %{date}'
            values={{ date: dateTimeToDashDateString(playerAccount.invitedAt) }}
          />
        </div>
      </div>
    )
  }

  const rows: TableRow[] = []
  const customClassRows: number[] = []
  businessPlayerAccounts?.players.forEach((dataRow, index) => {
    const row = dataRow as BussinessPlayerAccount
    const tableRow: CellElement[] = [
      <div
        className={classNames(
          styles.cell,
          styles.nickNameCell,
          row.inviteStatus === PlayerAccountInviteStatus.EXPIRED && styles.cellExp,
        )}
      >
        {row.nickName}
      </div>,
      <div
        className={classNames(
          styles.cell,
          styles.emailCell,
          row.inviteStatus === PlayerAccountInviteStatus.EXPIRED && styles.cellExp,
        )}
        dangerouslySetInnerHTML={{ __html: softBreakToEmail(row.email) }}
      />,
      createTagCell(row),
      createStatusCell(row),
      createActionCell(row),
    ]
    rows.push(tableRow)
    if (row.inviteStatus === PlayerAccountInviteStatus.EXPIRED) {
      customClassRows.push(index)
    }
  })
  return { rows, customClassRows }
}
